import React, { useContext, useEffect, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, VStack, Input, Button, useToast, Select, Badge, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Checkbox, SimpleGrid, Card, CardBody, Text, Flex, Divider, Icon, Stack, useBreakpointValue, IconButton, CardHeader } from "@chakra-ui/react";
import { FaUser, FaPhone, FaBuilding, FaHashtag, FaEdit, FaTrash } from "react-icons/fa";
import { Navigate } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";

export default function AdminEmployees() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [resellerFilter, setResellerFilter] = useState("");
  const [managerFilter, setManagerFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);
  const toast = useToast();

  const createDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const [editEmployee, setEditEmployee] = useState(null);

  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    reseller: "",
    phone_number: "",
    internal_number: "",
    is_manager: false,
  });

  const [editForm, setEditForm] = useState({
    reseller: "",
    phone_number: "",
    internal_number: "",
    is_manager: false,
  });

  useEffect(() => {
    if (!isSuperUser) return;
    fetchResellers();
    fetchEmployees(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser]);

  const fetchResellers = async () => {
    try {
      const { data } = await client.get(`/resellers/`);
      setResellers(data.results || data);
    } catch (e) {
      // Sessiz geç
    }
  };

  const fetchEmployees = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (searchTerm) params.set("search", searchTerm);
      if (resellerFilter) params.set("reseller", resellerFilter);
      if (managerFilter === "yes") params.set("is_manager", "true");
      if (managerFilter === "no") params.set("is_manager", "false");
      const { data } = await client.get(`/employees/?${params.toString()}`);
      const items = data.results || data;
      setEmployees(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      toast({ title: "Personellar alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (emp) => {
    const resolved = resolveEmpId(emp);
    setEditEmployee({ ...emp, _id: resolved });
    setEditForm({
      reseller: emp.reseller?.url || "",
      phone_number: emp.phone_number || "",
      internal_number: emp.internal_number || "",
      is_manager: !!emp.is_manager,
    });
    editDisclosure.onOpen();
  };

  const resolveEmpId = (emp) => {
    const tryUrl = (u) => { try { return urlToId(u); } catch { return null; } };
    if (!emp) return null;
    const fromUrl =
      tryUrl(emp.url) ||
      tryUrl(emp.employee?.url) ||
      tryUrl(emp?.hyperlink) ||
      tryUrl(emp?.employee?.hyperlink);
    const fromId =
      emp.id || emp.pk || emp.employee?.id || emp.employee?.pk || emp.employee_id;
    return fromUrl || fromId || null;
  };

  const saveEdit = async () => {
    if (!editEmployee) return;
    const empId = editEmployee?._id || resolveEmpId(editEmployee);
    if (!empId) {
      toast({ title: "Güncelleme başarısız", description: "Personel ID bulunamadı", status: "error" });
      return;
    }
    try {
      const payload = {
        phone_number: editForm.phone_number || null,
        internal_number: editForm.internal_number || null,
        is_manager: !!editForm.is_manager,
      };
      if (editForm.reseller) payload.reseller = editForm.reseller;
      await client.put(`/employees/${empId}/`, payload);
      toast({ title: "Personel güncellendi", status: "success" });
      editDisclosure.onClose();
      setEditEmployee(null);
      fetchEmployees(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      toast({ title: "Güncelleme başarısız", description: desc, status: "error" });
    }
  };

  const createEmployee = async () => {
    try {
      const payload = {
        username: createForm.username,
        email: createForm.email,
        first_name: createForm.first_name,
        last_name: createForm.last_name,
        password: createForm.password,
        phone_number: createForm.phone_number || null,
        internal_number: createForm.internal_number || null,
        is_manager: !!createForm.is_manager,
      };
      if (createForm.reseller) payload.reseller = createForm.reseller;
      await client.post(`/employees/`, payload);
      toast({ title: "Personel oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setCreateForm({ username: "", email: "", first_name: "", last_name: "", password: "", reseller: "", phone_number: "", internal_number: "", is_manager: false });
      fetchEmployees(1);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      toast({ title: "Oluşturma başarısız", description: desc, status: "error" });
    }
  };

  const handleDelete = async (emp) => {
    if (!window.confirm("Bu Personelı silmek istediğinize emin misiniz?")) return;
    try {
      await client.delete(`/employees/${urlToId(emp.url)}/`);
      toast({ title: "Personel silindi", status: "success" });
      fetchEmployees(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      toast({ title: "Silme başarısız", description: desc, status: "error" });
    }
  };

  const isMobile = useBreakpointValue({ base: true, md: false });

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={isMobile ? 2 : 6}>
      <Heading size="md" mb={4}>Personel Yönetimi</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <Stack direction={isMobile ? "column" : "row"} spacing={3}>
          <Input placeholder="Ara: ad, e-posta, kullanıcı adı" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onBlur={() => fetchEmployees(1)} />
          <Select placeholder="Bayi" value={resellerFilter} onChange={(e) => setResellerFilter(e.target.value)} w={isMobile ? "100%" : "220px"}>
            {resellers.map((r) => <option key={r.url || r.name} value={urlToId(r.url)}>{r.name}</option>)}
          </Select>
          <Select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)} w={isMobile ? "100%" : "200px"}>
            <option value="all">Tümü</option>
            <option value="yes">Yöneticiler</option>
            <option value="no">Yönetici olmayanlar</option>
          </Select>
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchEmployees(1); }} w={isMobile ? "100%" : "auto"}>Filtrele</Button>
          <Button colorScheme="green" onClick={createDisclosure.onOpen} w={isMobile ? "100%" : "auto"}>Yeni Personel</Button>
          <ExportButton
            data={(employees || []).map(e => ({
              id: urlToId(e.url),
              ad_soyad: `${e.user?.first_name || ''} ${e.user?.last_name || ''}`.trim(),
              bayi: e.reseller?.name || '-',
              telefon: e.phone_number || '-',
              dahili_no: e.internal_number || '-',
              yonetici: e.is_manager ? 'Evet' : 'Hayır',
            }))}
            filename="calisanlar"
            columns={[
              { key: "id", label: "ID" },
              { key: "ad_soyad", label: "Ad Soyad" },
              { key: "bayi", label: "Bayi" },
              { key: "telefon", label: "Telefon" },
              { key: "dahili_no", label: "Dahili No" },
              { key: "yonetici", label: "Yönetici" },
            ]}
            sheetName="Personellar"
            excelOptions={{ title: "Personellar" }}
          />
        </Stack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        isMobile ? (
          <SimpleGrid columns={1} spacing={4}>
            {employees.map((e) => (
              <Card key={e.url} variant="outline" boxShadow="sm">
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack>
                      <Icon as={FaUser} color="blue.500" />
                      <Text fontWeight="bold" fontSize="lg">
                        {e.user?.first_name} {e.user?.last_name}
                      </Text>
                    </HStack>
                    <Badge colorScheme={e.is_manager ? "purple" : "gray"}>
                      {e.is_manager ? "Yönetici" : "Personel"}
                    </Badge>
                  </Flex>
                </CardHeader>
                <Divider />
                <CardBody py={3}>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Icon as={FaBuilding} color="gray.400" />
                      <Text fontSize="sm" color="gray.600">Bayi:</Text>
                      <Text fontSize="sm" fontWeight="medium">{e.reseller?.name || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Icon as={FaPhone} color="gray.400" />
                      <Text fontSize="sm" color="gray.600">Telefon:</Text>
                      <Text fontSize="sm" fontWeight="medium">{e.phone_number || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Icon as={FaHashtag} color="gray.400" />
                      <Text fontSize="sm" color="gray.600">Dahili:</Text>
                      <Text fontSize="sm" fontWeight="medium">{e.internal_number || '-'}</Text>
                    </HStack>

                    <HStack mt={4} justify="flex-end" spacing={2}>
                      <Button size="sm" leftIcon={<Icon as={FaEdit} />} colorScheme="blue" variant="outline" onClick={() => onEdit(e)}>
                        Düzenle
                      </Button>
                      <Button size="sm" leftIcon={<Icon as={FaTrash} />} colorScheme="red" variant="outline" onClick={() => handleDelete(e)}>
                        Sil
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Ad Soyad</Th>
                <Th>Bayi</Th>
                <Th>Telefon</Th>
                <Th>Dahili No</Th>
                <Th>Yönetici</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {employees.map((e) => (
                <Tr key={e.url}>
                  <Td>{e.user?.first_name} {e.user?.last_name}</Td>
                  <Td>{e.reseller?.name || '-'}</Td>
                  <Td>{e.phone_number || '-'}</Td>
                  <Td>{e.internal_number || '-'}</Td>
                  <Td><Badge colorScheme={e.is_manager ? "purple" : "gray"}>{e.is_manager ? "Evet" : "Hayır"}</Badge></Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => onEdit(e)}>Düzenle</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleDelete(e)}>Sil</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )
      )}

      <Box mt={4}>
        <Pagination path={"admin/calisanlar"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} onPageChange={(p) => fetchEmployees(p)} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Personel Oluştur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Kullanıcı Adı</FormLabel>
                <Input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>E-posta</FormLabel>
                <Input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şifre</FormLabel>
                <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Bayi</FormLabel>
                <Select placeholder="Seçiniz" value={createForm.reseller} onChange={(e) => setCreateForm({ ...createForm, reseller: e.target.value })}>
                  {resellers.map((r) => (
                    <option key={r.url} value={r.url}>{r.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <Input value={createForm.phone_number} onChange={(e) => setCreateForm({ ...createForm, phone_number: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Dahili No</FormLabel>
                <Input value={createForm.internal_number} onChange={(e) => setCreateForm({ ...createForm, internal_number: e.target.value })} />
              </FormControl>
              <FormControl>
                <Checkbox isChecked={createForm.is_manager} onChange={(e) => setCreateForm({ ...createForm, is_manager: e.target.checked })}>Yönetici</Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={createDisclosure.onClose}>Kapat</Button>
            <Button colorScheme="green" onClick={createEmployee}>Oluştur</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={() => { editDisclosure.onClose(); setEditEmployee(null); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Personeli Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Bayi</FormLabel>
                <Select placeholder="Seçiniz" value={editForm.reseller} onChange={(e) => setEditForm({ ...editForm, reseller: e.target.value })}>
                  {resellers.map((r) => (
                    <option key={r.url} value={r.url}>{r.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <Input value={editForm.phone_number} onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Dahili No</FormLabel>
                <Input value={editForm.internal_number} onChange={(e) => setEditForm({ ...editForm, internal_number: e.target.value })} />
              </FormControl>
              <FormControl>
                <Checkbox isChecked={editForm.is_manager} onChange={(e) => setEditForm({ ...editForm, is_manager: e.target.checked })}>Yönetici</Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { editDisclosure.onClose(); setEditEmployee(null); }}>Kapat</Button>
            <Button colorScheme="blue" onClick={saveEdit}>Kaydet</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
